import { theme } from '@Guidelines/Theme/theme';
import formatLongDate from '@UI/Helpers/formatLongDate';
import type IdentityProviderView from '@UI/Store/Sso/Views/IdentityProviderView';
import { css } from '@emotion/react';
import * as React from 'react';
import { Button, Password, Tag } from '@verclam/ui-kit';

const summaryRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.75rem;
	flex-wrap: wrap;
`;

const summaryLabelStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkSecondary};
`;

const revealedStyle = css`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 0.5rem;
`;

type Props = {
	readonly identityProvider: IdentityProviderView | null;
	readonly value: string;
	readonly onChange: (value: string) => void;
	readonly errorMessage: string | undefined;
	readonly isFormFieldValid: boolean;
};

/**
 * The secret is never re-served by the server (SEC-12): once a configuration exists, the field stays
 * collapsed behind "Remplacer" and only appears when the administrator asks to replace it. Cancelling
 * closes it again and clears whatever was typed, so an abandoned secret never reaches the save request.
 * Collapsing back on its own once a save settles is the caller's job, not this component's: it keys
 * this component on `clientSecretRegisteredAt` so a fresh timestamp remounts it with `isRevealed` back
 * to `false`, rather than reaching for an effect to reset local state from a prop.
 */
const ClientSecretField: React.FC<Props> = ({
	identityProvider,
	value,
	onChange,
	errorMessage,
	isFormFieldValid,
}) => {
	const [isRevealed, setIsRevealed] = React.useState(false);

	const handleReveal = React.useCallback(() => {
		setIsRevealed(true);
	}, []);

	const handleCancel = React.useCallback(() => {
		setIsRevealed(false);
		onChange('');
	}, [onChange]);

	if (identityProvider !== null && !isRevealed) {
		return (
			<div css={summaryRowStyle}>
				<p css={summaryLabelStyle}>Secret client</p>
				<Tag
					severity="secondary"
					value={`enregistré le ${formatLongDate(identityProvider.clientSecretRegisteredAt.toISOString())}`}
				/>
				<Button type="button" text label="Remplacer" onClick={handleReveal} />
			</div>
		);
	}

	return (
		<div css={revealedStyle}>
			<Password
				name="clientSecret"
				label="Secret client"
				subLabel={
					identityProvider === null
						? undefined
						: 'Laissez ce champ vide pour conserver le secret déjà enregistré.'
				}
				required={identityProvider === null}
				feedback={false}
				toggleMask
				autoComplete="new-password"
				value={value}
				onChange={onChange}
				errorMessage={errorMessage}
				isFormFieldValid={isFormFieldValid}
			/>
			{identityProvider !== null ? (
				<Button type="button" text size="sm" label="Annuler" onClick={handleCancel} />
			) : null}
		</div>
	);
};

export default ClientSecretField;
